import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { captureError } from '@/lib/sentry';

/**
 * Critical kill-switches for emergency circuit breakers
 * These flags can instantly disable problematic flows
 */
export type KillSwitchKey = 
  | 'enable_inscriptions'
  | 'enable_payments'
  | 'enable_opco_automation'
  | 'enable_email_sending'
  | 'enable_document_generation'
  | 'enable_level_assessment'
  | 'enable_enterprise_enrollment'
  | 'maintenance_mode'
  | 'subscriptions_module_enabled';

/**
 * Default values per flag key - CRITICAL for fail-safe behavior
 * maintenance_mode MUST default to false to avoid bricking the app
 */
export const DEFAULT_FLAG_VALUES: Record<KillSwitchKey, boolean> = {
  enable_inscriptions: true,
  enable_payments: true,
  enable_opco_automation: true,
  enable_email_sending: true,
  enable_document_generation: true,
  enable_level_assessment: true,
  enable_enterprise_enrollment: true,
  maintenance_mode: false, // CRITICAL: false by default to avoid bricking app
  subscriptions_module_enabled: true,
};

interface FeatureFlag {
  id: string;
  flag_key: string;
  enabled: boolean;
  description: string | null;
  category: string | null;
  priority: string | null;
  requires_confirmation: boolean | null;
}

/**
 * Hook to check kill-switches status
 * Uses React Query for caching with 30s stale time for quick response
 */
export function useKillSwitch(flagKey: KillSwitchKey) {
  const { data: flags, isLoading, error } = useQuery({
    queryKey: ['kill-switches'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_feature_flags')
        .select('*')
        .order('flag_key');
      
      if (error) throw error;
      return data as FeatureFlag[];
    },
    staleTime: 30 * 1000, // 30 seconds - quick refresh for kill-switches
    gcTime: 5 * 60 * 1000, // 5 minutes cache
  });

  const flag = flags?.find(f => f.flag_key === flagKey);
  
  // Use flag-specific default if not found or on error
  const defaultValue = DEFAULT_FLAG_VALUES[flagKey];
  
  return {
    isEnabled: flag?.enabled ?? defaultValue,
    isLoading,
    error,
    flag,
  };
}

/**
 * Hook to get all kill-switches at once
 * Useful for admin dashboard
 */
export function useAllKillSwitches() {
  const { data: flags, isLoading, refetch } = useQuery({
    queryKey: ['kill-switches'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_feature_flags')
        .select('*')
        .order('priority', { ascending: false })
        .order('flag_key');
      
      if (error) throw error;
      return data as FeatureFlag[];
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const isEnabled = (flagKey: KillSwitchKey): boolean => {
    const flag = flags?.find(f => f.flag_key === flagKey);
    return flag?.enabled ?? DEFAULT_FLAG_VALUES[flagKey];
  };

  const getByCategory = (category: string) => {
    return flags?.filter(f => f.category === category) ?? [];
  };

  return {
    flags: flags ?? [],
    isLoading,
    isEnabled,
    getByCategory,
    refetch,
  };
}

/**
 * Maintenance mode specific hook
 * Returns error to allow fail-open behavior in MaintenanceMode component
 */
export function useMaintenanceMode() {
  const { isEnabled, isLoading, error } = useKillSwitch('maintenance_mode');
  
  // Log errors to Sentry for visibility
  if (error) {
    captureError(error as Error, { context: 'maintenance-mode-fetch' });
  }
  
  return {
    isMaintenanceMode: isEnabled, // Note: maintenance_mode=true means site is in maintenance
    isLoading,
    error,
  };
}
